import type { StageView } from '@aira/shared';

interface StepperProps {
  stages: StageView[];
  selectedStage?: StageView['stage'] | null;
  onStageClick?: (stage: StageView) => void;
}

/**
 * Four-dot horizontal stepper shown above the email composer. Visually maps
 * the active backend stage so the user can see where AIRA is in the pipeline.
 */
export function Stepper({ stages, selectedStage, onStageClick }: StepperProps) {
  return (
    <ol className="flex items-center justify-between gap-2 px-1" aria-label="Draft progress">
      {stages.map((s, idx) => {
        const isLast = idx === stages.length - 1;
        const selected = selectedStage === s.stage;
        const dotClass =
          s.status === 'done'
            ? 'bg-sf-primary text-sf-secondary border-sf-primary'
            : s.status === 'active'
              ? 'bg-sf-primary-light text-sf-primary border-sf-primary animate-pulse'
              : s.status === 'failed'
                ? 'bg-red-600 text-white border-red-600'
                : 'bg-sf-surface text-sf-gray/60 border-sf-primary/30';
        const labelClass =
          s.status === 'active'
            ? 'text-sf-primary font-semibold'
            : s.status === 'failed'
              ? 'text-red-600 font-semibold'
              : s.status === 'done'
                ? 'text-sf-primary'
                : 'text-sf-gray/60';
        const connectorClass = s.status === 'done' ? 'bg-sf-primary' : 'bg-sf-primary/15';
        const content = (
          <>
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full border text-[10px] font-bold ${dotClass}`}
              aria-current={s.status === 'active' ? 'step' : undefined}
              title={s.description}
            >
              {s.status === 'done' ? '✓' : s.status === 'failed' ? '!' : idx + 1}
            </span>
            <span className={`text-[10px] uppercase tracking-wide ${labelClass}`}>{s.label}</span>
          </>
        );
        return (
          <li key={s.stage} className="flex items-center gap-2 flex-1 last:flex-none">
            {onStageClick ? (
              <button
                type="button"
                className={`flex cursor-pointer flex-col items-center gap-1 rounded-md px-1.5 py-1 transition-colors hover:bg-sf-primary-light/70 focus:outline-none focus:ring-2 focus:ring-sf-primary/40 ${
                  selected ? 'bg-sf-primary-light ring-1 ring-sf-primary/30' : ''
                }`}
                onClick={() => onStageClick(s)}
                aria-pressed={selected}
              >
                {content}
              </button>
            ) : (
              <div className="flex flex-col items-center gap-1">{content}</div>
            )}
            {!isLast && <div className={`h-px flex-1 ${connectorClass}`} />}
          </li>
        );
      })}
    </ol>
  );
}
